// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// atomKvsSync persisted atom; zustand `{state, version}` envelopes migrate
// forward through the schema union below.
import type { DocPackReadingClass } from '@listening/doc-pack';
import { DOC_PACK_READING_CLASS_DEFAULTS } from '@listening/doc-pack';
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { Schema, SchemaTransformation, Struct } from 'effect';
import type { AtomRegistry } from 'effect/reactivity';
import { useMemo } from 'react';
import { atomKvsSync, fieldwiseKvsFallback } from '../atoms/kvs';

export const docPackReadingClassToggleOptions = [
  { class: 'citation', label: 'Citations' },
  { class: 'cross-reference', label: 'Cross-references' },
  { class: 'caption', label: 'Captions' },
  { class: 'footnote', label: 'Footnotes' },
  { class: 'endnote', label: 'Endnotes' },
  { class: 'equation', label: 'Equations' },
  { class: 'table', label: 'Tables' },
  { class: 'header-footer', label: 'Headers and footers' },
  { class: 'page-number', label: 'Page numbers' },
  { class: 'reference-entry', label: 'References' },
] as const satisfies ReadonlyArray<{
  readonly class: DocPackReadingClass;
  readonly label: string;
}>;

const optionalBoolean = Schema.optionalKey(Schema.Boolean);

// The annotation is the exhaustiveness check: Schema's type parameter is
// invariant, so this fails to compile if a DocPackReadingClass is missing.
// `Codec` (not the narrower `Schema` interface) so the Encoded/service type
// views survive into the Union/decodeTo composition below.
const docPackReadingClassOverridesSchema: Schema.Codec<
  Partial<Record<DocPackReadingClass, boolean>>
> = Schema.Struct({
  body: optionalBoolean,
  heading: optionalBoolean,
  citation: optionalBoolean,
  'cross-reference': optionalBoolean,
  caption: optionalBoolean,
  footnote: optionalBoolean,
  endnote: optionalBoolean,
  equation: optionalBoolean,
  table: optionalBoolean,
  'header-footer': optionalBoolean,
  'page-number': optionalBoolean,
  artifact: optionalBoolean,
  'reference-entry': optionalBoolean,
});

export type DocPackReadingClassOverrides = Partial<
  Record<DocPackReadingClass, boolean>
>;

const docPackContentViewSchema = Schema.Literals(['text', 'pdf']);
export type DocPackContentView = typeof docPackContentViewSchema.Type;

const docPackReadingPreferencesStateSchema = Schema.Struct({
  globalOverrides: docPackReadingClassOverridesSchema,
  documentOverrides: Schema.Record(
    Schema.String,
    docPackReadingClassOverridesSchema,
  ),
  contentViews: Schema.Record(Schema.String, docPackContentViewSchema),
});

export type DocPackReadingPreferencesState =
  typeof docPackReadingPreferencesStateSchema.Type;

const defaultDocPackReadingPreferences: DocPackReadingPreferencesState = {
  globalOverrides: {},
  documentOverrides: {},
  contentViews: {},
};

// zustand-persist stored a `{state, version}` envelope under the same key,
// and `state` can be PARTIAL — a user's last write may predate a field
// (zustand merged persisted-over-defaults on rehydrate). The transform is the
// migration (effect-atom has no version/migrate hook): decode accepts
// envelope or bare, partial or full, and merges over the defaults; encode
// writes the full bare shape (the envelope branch becomes dead a release
// after every user has written once). See plan doc §C2.
const partialDocPackReadingPreferencesSchema =
  docPackReadingPreferencesStateSchema.mapFields(
    Struct.map(Schema.optionalKey),
  );
const persistedDocPackReadingPreferencesSchema = Schema.Union([
  Schema.Struct({
    state: partialDocPackReadingPreferencesSchema,
    version: Schema.Int,
  }),
  partialDocPackReadingPreferencesSchema,
]).pipe(
  Schema.decodeTo(
    docPackReadingPreferencesStateSchema,
    SchemaTransformation.transform({
      decode: (stored) => ({
        ...defaultDocPackReadingPreferences,
        ...('state' in stored ? stored.state : stored),
      }),
      encode: (state) => state,
    }),
  ),
);

export const docPackReadingPreferencesAtom = atomKvsSync({
  key: 'doc-pack-reading-prefs-storage',
  schema: persistedDocPackReadingPreferencesSchema,
  defaultValue: () => defaultDocPackReadingPreferences,
  // A whole-blob decode failure (e.g. a retired reading-class key in a stored
  // override map) keeps the still-valid slices and defaults only the bad one.
  fieldFallback: fieldwiseKvsFallback({
    schema: docPackReadingPreferencesStateSchema,
    defaultValue: () => defaultDocPackReadingPreferences,
  }),
});

const withoutClass = (
  overrides: DocPackReadingClassOverrides,
  cls: DocPackReadingClass,
): DocPackReadingClassOverrides => {
  const { [cls]: _removed, ...rest } = overrides;
  return rest;
};

export const resolveDocPackReadingClassOverrides = (
  state: Pick<
    DocPackReadingPreferencesState,
    'globalOverrides' | 'documentOverrides'
  >,
  audioId?: string,
): DocPackReadingClassOverrides => ({
  ...state.globalOverrides,
  ...(audioId ? (state.documentOverrides[audioId] ?? {}) : {}),
});

export const resolveDocPackReadingClassEnabled = (
  overrides: DocPackReadingClassOverrides,
  cls: DocPackReadingClass,
) => overrides[cls] ?? DOC_PACK_READING_CLASS_DEFAULTS[cls];

export const resolveDocPackContentView = (
  state: Pick<DocPackReadingPreferencesState, 'contentViews'>,
  audioId?: string,
): DocPackContentView =>
  audioId ? (state.contentViews[audioId] ?? 'pdf') : 'pdf';

// Pure state transitions shared by the actions hook and tests. They update
// immutably and only replace the slices they touch — selectors and memo deps
// rely on untouched slices keeping their references (React #185 guard, see
// useDocPackReadingClassOverrides).
export const docPackReadingPreferencesUpdates = {
  setGlobalClassEnabled: (
    state: DocPackReadingPreferencesState,
    cls: DocPackReadingClass,
    enabled: boolean,
  ): DocPackReadingPreferencesState => ({
    ...state,
    globalOverrides: { ...state.globalOverrides, [cls]: enabled },
  }),
  resetGlobalClass: (
    state: DocPackReadingPreferencesState,
    cls: DocPackReadingClass,
  ): DocPackReadingPreferencesState => ({
    ...state,
    globalOverrides: withoutClass(state.globalOverrides, cls),
  }),
  setDocumentClassEnabled: (
    state: DocPackReadingPreferencesState,
    audioId: string,
    cls: DocPackReadingClass,
    enabled: boolean,
  ): DocPackReadingPreferencesState => ({
    ...state,
    documentOverrides: {
      ...state.documentOverrides,
      [audioId]: { ...state.documentOverrides[audioId], [cls]: enabled },
    },
  }),
  resetDocumentClass: (
    state: DocPackReadingPreferencesState,
    audioId: string,
    cls: DocPackReadingClass,
  ): DocPackReadingPreferencesState => ({
    ...state,
    documentOverrides: {
      ...state.documentOverrides,
      [audioId]: withoutClass(state.documentOverrides[audioId] ?? {}, cls),
    },
  }),
  setContentView: (
    state: DocPackReadingPreferencesState,
    audioId: string,
    contentView: DocPackContentView,
  ): DocPackReadingPreferencesState => ({
    ...state,
    contentViews: { ...state.contentViews, [audioId]: contentView },
  }),
  resetContentView: (
    state: DocPackReadingPreferencesState,
    audioId: string,
  ): DocPackReadingPreferencesState => {
    const { [audioId]: _removed, ...contentViews } = state.contentViews;
    return { ...state, contentViews };
  },
  resetDocument: (
    state: DocPackReadingPreferencesState,
    audioId: string,
  ): DocPackReadingPreferencesState => {
    const { [audioId]: _removedOverrides, ...documentOverrides } =
      state.documentOverrides;
    const { [audioId]: _removedContentView, ...contentViews } =
      state.contentViews;
    return { ...state, documentOverrides, contentViews };
  },
  resetAll: (): DocPackReadingPreferencesState =>
    defaultDocPackReadingPreferences,
};

export function useDocPackReadingPreferences(): DocPackReadingPreferencesState {
  return useAtomValue(docPackReadingPreferencesAtom);
}

export function useDocPackContentView(audioId?: string): DocPackContentView {
  return resolveDocPackContentView(useDocPackReadingPreferences(), audioId);
}

export function useDocPackReadingPreferencesActions() {
  const setPreferences = useAtomSet(docPackReadingPreferencesAtom);
  return useMemo(
    () => ({
      setGlobalClassEnabled: (cls: DocPackReadingClass, enabled: boolean) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.setGlobalClassEnabled(
            state,
            cls,
            enabled,
          ),
        );
      },
      resetGlobalClass: (cls: DocPackReadingClass) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.resetGlobalClass(state, cls),
        );
      },
      setDocumentClassEnabled: (
        audioId: string,
        cls: DocPackReadingClass,
        enabled: boolean,
      ) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.setDocumentClassEnabled(
            state,
            audioId,
            cls,
            enabled,
          ),
        );
      },
      resetDocumentClass: (audioId: string, cls: DocPackReadingClass) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.resetDocumentClass(
            state,
            audioId,
            cls,
          ),
        );
      },
      setContentView: (audioId: string, contentView: DocPackContentView) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.setContentView(
            state,
            audioId,
            contentView,
          ),
        );
      },
      resetContentView: (audioId: string) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.resetContentView(state, audioId),
        );
      },
      resetDocument: (audioId: string) => {
        setPreferences((state) =>
          docPackReadingPreferencesUpdates.resetDocument(state, audioId),
        );
      },
      resetAll: () => {
        setPreferences(docPackReadingPreferencesUpdates.resetAll());
      },
    }),
    [setPreferences],
  );
}

/**
 * Imperative reset for non-React callers (mobile user teardown). Writing the
 * defaults also persists them, replacing any user-scoped values in storage.
 */
export const resetDocPackReadingPreferences = (
  registry: AtomRegistry.AtomRegistry,
): void => {
  registry.set(docPackReadingPreferencesAtom, defaultDocPackReadingPreferences);
};

// NOTE: merge the two stable slices with useMemo — do NOT derive a fresh
// object inside a subscription selector. resolveDocPackReadingClassOverrides
// builds a new object per call; the memo keeps the returned reference stable
// across unrelated preference changes so consumers can use it in deps arrays.
export function useDocPackReadingClassOverrides(audioId?: string) {
  const state = useDocPackReadingPreferences();
  const globalOverrides = state.globalOverrides;
  const documentOverrides = audioId
    ? state.documentOverrides[audioId]
    : undefined;
  return useMemo(
    () => ({ ...globalOverrides, ...documentOverrides }),
    [globalOverrides, documentOverrides],
  );
}
